import React, { useState, useEffect } from 'react';
import { 
	Typography, Box,
	Card, CardHeader, CardContent, 
} from '@mui/material';
import "react-image-gallery/styles/css/image-gallery.css";
import moment from 'moment';
// import defaultLogo from '../assets/images/skyguide_blue_logo.png';
import { useTranslation } from 'react-i18next';
import { fetchAttendeeTypes } from '../api/connect';
import { CUSTOM_FIELDS } from './portal/portalConfigs';
// import { Link } from 'react-router-dom';

const classes = {
	cardContainer: {
		boxShadow: 'unset',
		'@media (max-width:900px)': {
			marginBottom: '5%'
		}
	},
	cardContent: {
		display: 'flex',
		alignItems: 'center',
		padding: 'unset',
		flexDirection: 'column',
		'&:last-child': {
			paddingleft: 8,
			paddingBottom: 0,
		},
	},
	cardInfo: {
		display: 'flex',
		alignItems: 'start',
		flexWrap: 'wrap',
		flexDirection: 'column',
	},
	cardInfoTitle: {
		fontWeight: '700',
		fontFamily: 'TT-Interphases-Regular',
		color: '#3D3D3D',
		fontStyle: 'normal',
		fontSize: '22px',
		margin: '10px 0',
		width: '100%',
		textAlign: 'left',
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		letterSpacing: '-0.3px',
	},
	cardInfoLine: {
		fontFamily: 'TT-Interphases-Regular',
		fontSize: '14px',
		fontWeight: '700',
		color: '#3E3E3E',
		marginBottom: '10px',
		textAlign: 'left',
		width: '100%',
	},
	media: {
		//height: '140px',
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		backgroundColor: '#ececec',
		// borderBottom: props => ` 5px solid ${categoryColor(props.event.category_id)}`,
	},
	eventLink: {
		textDecoration: 'none !important'
	},
	categoryLabel: {
		position: 'absolute',
		top: 0,
		color: 'white',
		borderRadius: '5px',
		height: '20px',
		padding: '0 10px',
		display: 'flex',
		margin: '10px',
		alignItems: 'center',
		// backgroundColor: props => `${categoryColor(props.event.category_id)}`,
		'& p': {
			padding: 'unset',
			margin: 'unset',
			textTransform: 'uppercase', 
			fontSize: '10px'
		}
	},
	categoryTab: {
		// backgroundColor: props => `${categoryColor(props.event.category_id)}`,
		display: 'inline-flex',
		alignItems: 'center',
		color: 'white',
		height: '30px',
		padding: '0 10px',
		textTransform: 'uppercase',
		fontSize: '10px'
	},
	registerUrl: {
		textDecoration: 'none',
		fontFamily: 'Lato, sans-serif',
		fontSize: '17px',
		fontWeight: '700'
	},
	tagsContainer: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'baseline',
		width: '100%',
		justifyContent: 'flex-start',
	},
	tagLanguage: {
		fontFamily: 'TT-Interphases-Regular',
		backgroundColor: '#a0bee6',
		width: '54px',
		height: '24px',
		color: 'white',
		fontSize: '14px',
		fontWeight: 'bold',
		textTransform: 'uppercase',
		'@media (max-width:1060px) and (min-width:900px)': {
			width: '40px',
		}
	},
	tagMilitary: {
		fontFamily: 'TT-Interphases-Regular',
		backgroundColor: '#202a44',
		width: '54px',
		height: '24px',
		color: 'white',
		fontSize: '14px',
		fontWeight: 'bold',
		marginLeft: '16px',
		'@media (max-width:1060px) and (min-width:900px)': {
			width: '40px',
		}
	},
};

const EventCard = (props) => {
	const event = props.event.attributes;
	const eventLanguage = event.fields?.[872208];
	const eventUri = event?.event_uri;
	const isMilitary = event.fields?.[872943]?.[0] === 'Yes';
	const seatsUnit = event.fields?.[CUSTOM_FIELDS.eventType] === 'Visits' ? 'groups' : 'places';
	const { t } = useTranslation();
	// Keyed by event uri so a recycled card instance never pairs one event's title
	// with another event's seat count. A failed or pending load simply leaves this
	// null, which hides the counter rather than claiming the event is sold out.
	const [loadedCapacity, setLoadedCapacity] = useState(null);
	const attendeeTypes = loadedCapacity?.uri === eventUri ? loadedCapacity.attendeeTypes : null;

	useEffect(() => {
		if (!eventUri) return;
		let cancelled = false;

		const loadCapacity = async () => {
			try {
				const attendeeTypes = await fetchAttendeeTypes(eventUri);
				if (cancelled) return;
				setLoadedCapacity({ uri: eventUri, attendeeTypes });
			} catch (error) {
				if (cancelled) return;
				console.warn(`EventCard: capacity unavailable for ${eventUri}`, error);
			}
		};

		loadCapacity();
		return () => { cancelled = true; };
	// `event` belongs in the deps even though only `eventUri` is read: the parent
	// hands over a fresh object on every list reload, which is the signal to
	// refresh seat counts that may have changed since the first load.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [event, eventUri]);

	// Seat limits live on the attendee types, not on the event: `venue_capacity` is
	// the venue's headcount and is null on virtually every event, so it cannot be
	// read as "unlimited". Only a type with a real, finite capacity carries a limit;
	// a type with none set (null or 0) is unbounded and is left out of the count. If
	// no type has a capacity there is nothing to report and the counter stays hidden.
	const limitedAttendeeTypes = (attendeeTypes || []).filter((attendeeType) => {
		const capacity = attendeeType?.attributes?.capacity;
		return typeof capacity === 'number' && capacity > 0;
	});

	const totalAvailableSeats = limitedAttendeeTypes.reduce((total, { attributes }) => {
		const { capacity, availability, used_seats } = attributes || {};
		// `availability` is capacity minus used seats, already computed by the API.
		const seatsLeft = availability ?? ((capacity || 0) - (used_seats || 0));
		// Back-office registrations can oversell a type; a negative leftover must not
		// cancel out seats the other types still have.
		return total + Math.max(0, seatsLeft || 0);
	}, 0);

	const showSeats = limitedAttendeeTypes.length > 0;

	const tagLanguage = (lang) => {
		switch(lang) {
			case 'English':
				return 'EN';
			case 'French':
				return 'FR';
			case 'Italian':
				return 'IT';
			case 'German':
				return 'DE';
			default:
				return 'EN';
		};
	};

	let cardEventHeader = (event) => {
		return(
			<Box sx={classes.media}>
				{headerImg()}
				{event.category_name !== null ? (
					<div className={classes.categoryLabel} ><p>{event.category_name}</p></div>
				) : (
					null
				)}
			</Box>
		)
	}

	let headerImg = () => {
		let url = ''
		let eventEndDate = Date.parse(event.end_date)
		let minEndDate = Date.parse('Apr 07, 2020')
		if(event.banner_image?.medium !== null && eventEndDate < minEndDate){
			url = event.banner_image.large_url?.replace('/large/', '/original/');
			return <img src={url} alt='img' style={{objectFit: 'cover', aspectRatio: '2 / 1', width: '100%'}} />
		}
		if(event.logo?.medium !== null){
			url = event.logo.medium;
			if(url.includes('missing')) {
				return (
					<div
						className='event-img-container'
						style={{
							display: 'flex',
							justifyContent: 'center',
							width:'100%',
							aspectRatio: '2 / 1',
							backgroundColor: '#d1cdcd',
						}}>
						{/* <img src={defaultLogo} alt='default logo' style={{objectFit: 'contain', aspectRatio: '2 / 1', width: '100%'}} /> */}
					</div>
				);
			};
			return <img src={url} alt='img' style={{objectFit: 'cover', aspectRatio: '2 / 1', width: '100%'}} />
		}
	}

	return (
		<Card sx={classes.cardContainer}>
			<CardHeader
				component={() => cardEventHeader(event)}
			/>
			<Typography style={classes.cardInfoTitle}>
				{event.name}
			</Typography>
			<CardContent sx={classes.cardContent}>
				{showSeats ? (
					<Typography variant="body2" color="textSecondary" component="div" sx={classes.cardInfoLine}>	
						{totalAvailableSeats >= 1 ? (
							`${totalAvailableSeats} ${seatsUnit} available`
						) : (
							`No ${seatsUnit} available`
						)}
					</Typography>
				) : null}
				{!props.hideEventDates && (
					<Typography variant="body2" color="textSecondary" component="div" sx={classes.cardInfoLine}>
						{moment(event.start_date).format('DD.MM.YYYY H:mm')} {t('card.until')} {moment(event.end_date).format('DD.MM.YYYY H:mm')}
					</Typography>
				)}
				<Box sx={classes.tagsContainer}>
					<Box sx={classes.tagLanguage}>{tagLanguage(eventLanguage)}</Box>
					{isMilitary && (
						<Box sx={classes.tagMilitary}>MIL*</Box>
					)}
				</Box>
			</CardContent>
		</Card>
	)
}

export default (EventCard);